import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';

import {
  BULK_CASE_ACTION_ENDPOINT,
  BULK_CASE_ACTION_COUNT_ENDPOINT,
  BULK_CASE_ACTION_RUN_ENDPOINT,
  BULK_CASE_ACTION_STATUS_ENDPOINT,
} from '@app/constants/endpoints';
import {
  BULK_CASE_ACTION_KIND,
  type BulkCaseActionKind,
} from '@app/constants/queue';
import { QUERY_KEYS } from '@app/constants/service';
import { fetchData, postData } from '@app/hooks/react-query-helpers';

export interface BulkQueueFilter {
  isOpen?: boolean;
  operatorIds?: string[];
  groupIds?: string[];
  workflowStatus?: string;
  queueFilter?: string;
  exposureTypeIds?: string[];
  caseIds?: string[];
}

export interface BulkQueueScopeCount {
  totalMatched: number;
  notVisible: number;
}

export type BulkQueueSubmitRequest = { filter: BulkQueueFilter } & (
  | {
      kind: typeof BULK_CASE_ACTION_KIND.QUEUE_SET;
      payload: { queueId: string };
    }
  | {
      kind: typeof BULK_CASE_ACTION_KIND.QUEUE_REMOVE;
      payload: Record<string, never>;
    }
);

export interface BulkQueueSubmitResult extends BulkQueueScopeCount {
  runId: string;
}

export interface BulkQueueOrgStatus {
  inProgress: boolean;
  enabled: boolean;
  kind: BulkCaseActionKind | null;
}

export interface BulkQueueRunComplete {
  runId: string;
  status: string;
  kind: BulkCaseActionKind;
  payload: Record<string, unknown>;
  snapshotSize: number;
  notVisible: number;
  succeededCount: number;
  skippedCount: number;
  failedCount: number;
  sideEffectFailures: number;
  startedAt?: string;
  finishedAt?: string;
}

export interface BulkQueueRunItem {
  caseId: string;
  status: string;
  reasonKey?: string;
}

export interface BulkQueueRunPagination {
  count: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  nextPage: number | null;
  prevPage: number | null;
}

export interface BulkQueueRunStatus extends BulkQueueRunComplete {
  items: BulkQueueRunItem[];
  pagination: BulkQueueRunPagination;
}

// A run lasts minutes and every queue admin polls this.
const BULK_QUEUE_STATUS_POLL_INTERVAL_MS = 30000;

export const FIRST_RUN_ITEMS_PAGE = 1;

export const useBulkQueueCountQuery = (
  filter: BulkQueueFilter | null,
  enabled = true,
) =>
  useQuery<BulkQueueScopeCount>({
    queryKey: [QUERY_KEYS.BULK_QUEUE_COUNT, filter],
    queryFn: async () =>
      postData(
        BULK_CASE_ACTION_COUNT_ENDPOINT,
        { filter },
        { handleError: true },
      ),
    enabled: enabled && Boolean(filter),
    // A stale count would let an admin commit to a scope the filter no longer matches.
    staleTime: 0,
  });

export const BULK_QUEUE_CASES_CHANGED_KEY = [
  QUERY_KEYS.BULK_QUEUE_CASES_CHANGED,
];

export const useBulkQueueSubmitMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<BulkQueueSubmitResult, Error, BulkQueueSubmitRequest>({
    mutationFn: async (request) =>
      postData(BULK_CASE_ACTION_ENDPOINT, request, { handleError: true }),
    onSuccess: ({ runId }, { kind }) => {
      const finishedRunId = queryClient.getQueryData<string | null>(
        BULK_QUEUE_CASES_CHANGED_KEY,
      );
      if (finishedRunId === runId) return;
      queryClient.setQueryData<BulkQueueOrgStatus>(
        [QUERY_KEYS.BULK_QUEUE_STATUS],
        (status) => (status ? { ...status, inProgress: true, kind } : status),
      );
    },
  });
};

export const useBulkQueueOrgStatusQuery = (enabled: boolean) =>
  useQuery<BulkQueueOrgStatus>({
    queryKey: [QUERY_KEYS.BULK_QUEUE_STATUS],
    queryFn: async () => fetchData(BULK_CASE_ACTION_STATUS_ENDPOINT),
    enabled,
    refetchInterval: BULK_QUEUE_STATUS_POLL_INTERVAL_MS,
  });

// Websocket handlers run outside React with a queryClient but no store to write to.
const CLIENT_STATE_OPTIONS = {
  enabled: false,
  staleTime: Infinity,
  cacheTime: Infinity,
};

export const BULK_QUEUE_RESULT_RUN_KEY = [QUERY_KEYS.BULK_QUEUE_RESULT_RUN];

export const setBulkQueueResultRun = (
  queryClient: QueryClient,
  runId: string | null,
): void => {
  queryClient.setQueryData(BULK_QUEUE_RESULT_RUN_KEY, runId);
};

export const useBulkQueueResultRun = (): string | null =>
  useQuery<string | null>({
    queryKey: BULK_QUEUE_RESULT_RUN_KEY,
    queryFn: () => null,
    initialData: null,
    ...CLIENT_STATE_OPTIONS,
  }).data ?? null;

// The run id, not a counter: one run arrives on two topics and must reload once.
export const signalBulkQueueCasesChanged = (
  queryClient: QueryClient,
  runId?: string | null,
): void => {
  queryClient.setQueryData<string | null>(
    BULK_QUEUE_CASES_CHANGED_KEY,
    runId ?? null,
  );
};

export const useBulkQueueCasesChangedSignal = (): string | null =>
  useQuery<string | null>({
    queryKey: BULK_QUEUE_CASES_CHANGED_KEY,
    queryFn: () => null,
    initialData: null,
    ...CLIENT_STATE_OPTIONS,
  }).data;

export const BULK_QUEUE_CASES_RELOADED_KEY = [
  QUERY_KEYS.BULK_QUEUE_CASES_RELOADED,
];

// The signal outlives the Cases page, so a remount must not replay a finished run.
export const markBulkQueueCasesReloaded = (
  queryClient: QueryClient,
  runId: string,
): void => {
  queryClient.setQueryData<string | null>(BULK_QUEUE_CASES_RELOADED_KEY, runId);
};

export const useBulkQueueCasesReloadedRunId = (): string | null =>
  useQuery<string | null>({
    queryKey: BULK_QUEUE_CASES_RELOADED_KEY,
    queryFn: () => null,
    initialData: null,
    ...CLIENT_STATE_OPTIONS,
  }).data;

export const fetchAllBulkQueueRunItems = async (
  runId: string,
): Promise<BulkQueueRunItem[]> => {
  const items: BulkQueueRunItem[] = [];
  let page: number | null = FIRST_RUN_ITEMS_PAGE;
  while (page !== null) {
    // eslint-disable-next-line no-await-in-loop -- each page's cursor comes from the last response
    const response: BulkQueueRunStatus = await fetchData(
      BULK_CASE_ACTION_RUN_ENDPOINT(runId, page),
    );
    items.push(...(response?.items ?? []));
    const nextPage = response?.pagination?.nextPage ?? null;
    // A nextPage that never advances would loop forever.
    page = nextPage !== null && nextPage > page ? nextPage : null;
  }
  return items;
};

export const useBulkQueueRunStatusQuery = (
  runId: string | null,
  page: number,
) =>
  useQuery<BulkQueueRunStatus>({
    queryKey: [QUERY_KEYS.BULK_QUEUE_RUN, runId, page],
    queryFn: async () =>
      fetchData(BULK_CASE_ACTION_RUN_ENDPOINT(runId as string, page)),
    enabled: Boolean(runId),
  });
